import { Component, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppBarModule } from '@progress/kendo-angular-navigation';
import {
  selectAllDomains,
  selectCurrentDomainId,
} from '../../store/entities/domains/domains.selectors';
import { setCurrentDomainId } from '../../store/entities/domains/domains.actions';
import { selectUser } from '../../store/entities/user/user.selectors';
import { selectProfile } from '../../store/entities/profile/profile.selectors';
import { Domain } from '../../store/entities/models/domain.model';
import { DomainSelectorComponent } from '../../shared/components/domain-selector/domain-selector.component';
import { LogoComponent } from '../../shared/components/logo/logo.component';
import { UserMenuComponent } from '../../shared/components/user-menu/user-menu.component';
import { Auth0Service } from '../../core/auth/services/auth0.service';

@Component({
  selector: 'app-topbar-kendo',
  standalone: true,
  imports: [
    CommonModule,
    AppBarModule,
    LogoComponent,
    DomainSelectorComponent,
    UserMenuComponent,
  ],
  templateUrl: './topbar-kendo.component.html',
  styleUrl: './topbar-kendo.component.css',
})
export class TopbarKendoComponent implements OnDestroy {
  private store = inject(Store);
  private router = inject(Router);
  private auth0Service = inject(Auth0Service);
  private destroy$ = new Subject<void>();

  user$ = this.store.select(selectUser);
  profile$ = this.store.select(selectProfile);
  domains$: Observable<readonly Domain[]> = this.store.select(selectAllDomains);
  currentDomainId$: Observable<string | null> = this.store.select(selectCurrentDomainId);

  userData$ = combineLatest([this.user$, this.profile$]).pipe(
    map(([user, profile]) => {
      const {
        firstName: profileFirstName,
        lastName: profileLastName,
        fullName: profileFullName,
        email: profileEmail,
        photo: profilePhoto,
      } = profile ?? {};
      const {
        firstName: userFirstName,
        lastName: userLastName,
        fullName: userFullName,
        email: userEmail,
      } = user ?? {};

      let fullName = 'User';

      if (profileFullName) {
        fullName = profileFullName;
      } else if (profileFirstName && profileLastName) {
        fullName = `${profileFirstName} ${profileLastName}`;
      } else if (userFullName) {
        fullName = userFullName;
      } else if (userFirstName && userLastName) {
        fullName = `${userFirstName} ${userLastName}`;
      }

      const email = profileEmail || userEmail || '';
      const photo = profilePhoto ?? null;

      const initials = this.getInitials(fullName);

      return { fullName, email, photo, initials };
    })
  );

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDomainChange(domainId: string): void {
    this.store.dispatch(setCurrentDomainId({ domainId }));
  }

  onAccountClick(): void {
    this.router.navigate(['/account']);
  }

  async onLogoutClick(): Promise<void> {
    await this.auth0Service.logout();
  }

  private getInitials(fullName: string): string {
    if (!fullName || fullName === 'User') {
      return 'U';
    }

    const parts = fullName.trim().split(' ');

    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }

    return fullName.charAt(0).toUpperCase();
  }
}
